import { useCallback, useEffect, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { meApi } from '../services/api';
import { usePlatformEvents } from '../contexts/PlatformEventContext';
import type { RunSummary, ToolCallDetail } from '../types/runs';
import type { RunStatus, RunStepResponse } from '../types';
import type { PlatformEvent } from '../types/events';
import { stepsToToolCalls } from '../utils/content-builder';
import type {
  RunCompletedData,
  RunFailedData,
  RunToolCallData,
  RunToolResultData,
  RunResponseDeltaData,
  RunResponseStopData,
  RunFinalAnswerDeltaData,
  RunFinalAnswerStopData,
  RunReasoningStopData,
  RunSkillActivatedData,
  RunSkillDeactivatedData,
  RunSubagentInvokedData,
  RunSubagentStartedData,
  RunSubagentCompletedData,
  RunSubagentReturnedData,
} from '../types/events';

const PAGE_SIZE = 30;
const REALTIME_CAP = 200;
const PREVIEW_CAP = 200;

function statusResponseToSummary(r: {
  runId: string;
  agentId: string;
  agentName?: string;
  conversationId?: string;
  status: RunStatus;
  response?: string;
  responsePreview?: string;
  steps?: RunStepResponse[];
  currentIteration?: number;
  durationMs?: number;
  usage?: { inputTokens: number; outputTokens: number; totalTokens: number };
  error?: string;
  parentRunId?: string;
  executionMode?: 'coordinator' | 'subagent' | 'delegated' | 'standalone' | 'direct';
  createdAt: string;
  completedAt?: string;
  startedAt?: string;
  spaceId?: string;
  runtime?: import('../types').RunRuntime;
}): RunSummary {
  const steps = r.steps ?? [];
  const toolCalls: ToolCallDetail[] = stepsToToolCalls(steps).map((tc) => ({
    toolCallId: tc.toolCallId,
    toolName: tc.toolName,
    input: tc.input,
    output: tc.output,
    error: tc.error,
    durationMs: tc.durationMs,
    status: tc.error ? 'error' as const : 'complete' as const,
  }));
  const toolNames = [...new Set(toolCalls.map((tc) => tc.toolName))];
  // List endpoints return `responsePreview` (~200 chars); single-run reads return the full `response`.
  const preview = r.responsePreview ?? (r.response ? r.response.slice(0, PREVIEW_CAP) : '');
  return {
    runId: r.runId,
    agentId: r.agentId,
    agentName: r.agentName,
    conversationId: r.conversationId,
    status: r.status,
    response: r.response,
    responsePreview: preview,
    isStreaming: false,
    toolCallCount: toolCalls.length,
    toolCallNames: toolNames,
    toolCalls,
    steps,
    currentIteration: r.currentIteration,
    usage: r.usage,
    durationMs: r.durationMs,
    error: r.error,
    parentRunId: r.parentRunId,
    executionMode: r.executionMode,
    createdAt: r.createdAt,
    updatedAt: r.completedAt ?? r.startedAt ?? r.createdAt,
    spaceId: r.spaceId,
    runtime: r.runtime,
  };
}

/** Find the index of the most recent step matching `type` within the given iteration. */
function findLastStepIndex(
  steps: RunStepResponse[],
  type: RunStepResponse['type'],
  iteration: number,
): number {
  for (let i = steps.length - 1; i >= 0; i--) {
    if (steps[i].type === type && steps[i].iteration === iteration) return i;
  }
  return -1;
}

/** Stringify a tool result output into the persisted-step shape (always string). */
function stringifyOutput(output: unknown): string | undefined {
  if (output == null) return undefined;
  if (typeof output === 'string') return output;
  try {
    return JSON.stringify(output);
  } catch {
    return String(output);
  }
}

/** Synthesize a fresh RunSummary skeleton from whatever fields a non-`started` event carries.
 *  Used when an event arrives for a run that isn't in the realtime map yet (no API fetch). */
function synthesizeRunFromEvent(
  runId: string,
  eventType: string,
  d: Record<string, unknown>,
  now: string,
): RunSummary {
  let status: RunStatus = 'running';
  if (eventType === 'agent.run.completed') status = 'completed';
  else if (eventType === 'agent.run.failed') status = 'failed';
  else if (eventType === 'agent.run.cancelled') status = 'cancelled';
  return {
    runId,
    agentId: (d.agentId as string) ?? '',
    agentName: d.agentName as string | undefined,
    conversationId: d.conversationId as string | undefined,
    status,
    responsePreview: '',
    isStreaming: false,
    toolCallCount: 0,
    toolCallNames: [],
    toolCalls: [],
    steps: [],
    createdAt: now,
    updatedAt: now,
    spaceId: d.spaceId as string | undefined,
    runtime: d.runtime as import('../types').RunRuntime | undefined,
  };
}

/** Apply an event to an existing RunSummary, returning an updated copy (or null if no change). */
function applyEvent(
  existing: RunSummary,
  eventType: string,
  d: Record<string, unknown>,
  now: string,
): RunSummary | null {
  // ── Tool calls ────────────────────────────────────────────────────────────
  if (eventType === 'agent.run.tool_call') {
    const tc = d as unknown as RunToolCallData;
    const names = existing.toolCallNames.includes(tc.toolName)
      ? existing.toolCallNames
      : [...existing.toolCallNames, tc.toolName];
    const newStep: RunStepResponse = {
      type: 'tool_call',
      iteration: tc.iteration,
      timestamp: tc.timestamp ?? now,
      toolName: tc.toolName,
      toolDisplayName: tc.toolDisplayName,
      toolCallId: tc.toolCallId,
      input: tc.input,
    };
    return {
      ...existing,
      toolCallCount: existing.toolCallCount + 1,
      toolCallNames: names,
      toolCalls: [...existing.toolCalls, {
        toolCallId: tc.toolCallId,
        toolName: tc.toolName,
        input: tc.input,
        status: 'pending',
      }],
      steps: [...existing.steps, newStep],
      currentIteration: tc.iteration,
      updatedAt: now,
    };
  }

  if (eventType === 'agent.run.tool_result') {
    const tr = d as unknown as RunToolResultData;
    const updatedToolCalls = existing.toolCalls.map((tc) =>
      tc.toolCallId === tr.toolCallId
        ? { ...tc, output: tr.output, error: tr.error, status: (tr.error ? 'error' : 'complete') as ToolCallDetail['status'] }
        : tc
    );
    const updatedSteps = existing.steps.map((s) => {
      if (s.type === 'tool_call' && s.toolCallId === tr.toolCallId) {
        return {
          ...s,
          output: stringifyOutput(tr.output),
          error: tr.error,
          artifacts: tr.artifacts,
          durationMs: s.durationMs ?? (tr as { durationMs?: number }).durationMs,
        };
      }
      return s;
    });
    return { ...existing, toolCalls: updatedToolCalls, steps: updatedSteps, updatedAt: now };
  }

  // ── Reasoning ─────────────────────────────────────────────────────────────
  if (eventType === 'agent.run.reasoning_start') {
    // No content yet — just note the iteration.
    return { ...existing, currentIteration: (d.iteration as number) ?? existing.currentIteration, updatedAt: now };
  }

  if (eventType === 'agent.run.reasoning_delta') {
    // Deltas are ignored — content arrives in reasoning_stop (pending backend update).
    return null;
  }

  if (eventType === 'agent.run.reasoning_stop') {
    const rs = d as unknown as RunReasoningStopData;
    if (!rs.content) {
      // No content (older payload or empty reasoning) — reconcile via
      // `agent.run.completed` instead.
      return { ...existing, updatedAt: now };
    }
    return {
      ...existing,
      steps: [...existing.steps, {
        type: 'reasoning',
        iteration: rs.iteration,
        timestamp: rs.timestamp ?? now,
        text: rs.content,
      }],
      currentIteration: rs.iteration,
      updatedAt: now,
    };
  }

  // ── Response (intermediate narration) ─────────────────────────────────────
  if (eventType === 'agent.run.response_start') {
    return { ...existing, isStreaming: true, updatedAt: now };
  }

  if (eventType === 'agent.run.response_delta') {
    // Update the running preview but DO NOT touch the steps array.
    const rd = d as unknown as RunResponseDeltaData;
    let preview = existing.responsePreview;
    if (rd.accumulated != null) {
      preview = rd.accumulated.slice(0, PREVIEW_CAP);
    } else if (rd.delta) {
      preview = (preview + rd.delta).slice(0, PREVIEW_CAP);
    }
    return { ...existing, responsePreview: preview, updatedAt: now };
  }

  if (eventType === 'agent.run.response_stop') {
    const rs = d as unknown as RunResponseStopData;
    if (!rs.content) {
      return { ...existing, isStreaming: false, updatedAt: now };
    }
    // Idempotency: if a narration step already exists for this iteration
    // (e.g. duplicate stop event), update in place rather than appending.
    const idx = findLastStepIndex(existing.steps, 'narration', rs.iteration);
    let newSteps: RunStepResponse[];
    if (idx === -1) {
      newSteps = [...existing.steps, {
        type: 'narration',
        iteration: rs.iteration,
        timestamp: rs.timestamp ?? now,
        text: rs.content,
      }];
    } else {
      newSteps = [...existing.steps];
      newSteps[idx] = { ...newSteps[idx], text: rs.content, timestamp: rs.timestamp ?? newSteps[idx].timestamp ?? now };
    }
    return { ...existing, isStreaming: false, steps: newSteps, updatedAt: now };
  }

  // ── Final answer ──────────────────────────────────────────────────────────
  if (eventType === 'agent.run.final_answer_start') {
    return { ...existing, isStreaming: true, response: '', responsePreview: '', updatedAt: now };
  }

  if (eventType === 'agent.run.final_answer_delta') {
    const fd = d as unknown as RunFinalAnswerDeltaData;
    let preview = existing.responsePreview;
    let response = existing.response ?? '';
    if (fd.accumulated != null) {
      preview = fd.accumulated.slice(0, PREVIEW_CAP);
      response = fd.accumulated;
    } else if (fd.delta) {
      preview = (preview + fd.delta).slice(0, PREVIEW_CAP);
      response = response + fd.delta;
    }
    return { ...existing, response, responsePreview: preview, updatedAt: now };
  }

  if (eventType === 'agent.run.final_answer_stop') {
    const fs = d as unknown as RunFinalAnswerStopData;
    if (!fs.content) {
      return { ...existing, isStreaming: false, updatedAt: now };
    }
    // The last narration step is treated as the final answer in persisted runs
    // (see src/utils/content-builder.ts:174-181), so we mirror that here.
    const idx = findLastStepIndex(existing.steps, 'narration', fs.iteration);
    let newSteps: RunStepResponse[];
    if (idx === -1) {
      newSteps = [...existing.steps, {
        type: 'narration',
        iteration: fs.iteration,
        timestamp: fs.timestamp ?? now,
        text: fs.content,
      }];
    } else {
      newSteps = [...existing.steps];
      newSteps[idx] = { ...newSteps[idx], text: fs.content, timestamp: fs.timestamp ?? newSteps[idx].timestamp ?? now };
    }
    return {
      ...existing,
      isStreaming: false,
      response: fs.content,
      responsePreview: fs.content.slice(0, PREVIEW_CAP),
      usage: fs.usage
        ? {
            inputTokens: fs.usage.inputTokens,
            outputTokens: fs.usage.outputTokens,
            totalTokens: fs.usage.totalTokens ?? fs.usage.inputTokens + fs.usage.outputTokens,
          }
        : existing.usage,
      steps: newSteps,
      updatedAt: now,
    };
  }

  // ── Skills (single events; synthesised as completed tool calls) ───────────
  if (eventType === 'agent.run.skill_activated' || eventType === 'agent.run.skill_deactivated') {
    const s = d as unknown as RunSkillActivatedData | RunSkillDeactivatedData;
    const toolName = eventType === 'agent.run.skill_activated' ? 'activate_skill' : 'deactivate_skill';
    const toolCallId = `${toolName}-${s.skillId}-${s.iteration}`;
    const step: RunStepResponse = {
      type: 'tool_call',
      iteration: s.iteration,
      timestamp: s.timestamp ?? now,
      toolName,
      toolCallId,
      input: { skillId: s.skillId, skillName: s.skillName },
      output: s.success ? 'ok' : undefined,
      error: s.error,
      durationMs: s.durationMs,
    };
    return {
      ...existing,
      steps: [...existing.steps, step],
      toolCallCount: existing.toolCallCount + 1,
      toolCalls: [...existing.toolCalls, {
        toolCallId,
        toolName,
        input: { skillId: s.skillId, skillName: s.skillName },
        output: step.output,
        error: step.error,
        durationMs: step.durationMs,
        status: s.success ? 'complete' : 'error',
      }],
      currentIteration: s.iteration,
      updatedAt: now,
    };
  }

  // ── Sub-agents (synthesised as __subagent_spawn / __subagent_run tool calls) ──
  // RunDetailPanel.tsx:60-103 detects these special tool names at render time
  // and groups them into a sub-agent block.
  if (eventType === 'agent.run.subagent_invoked') {
    const si = d as unknown as RunSubagentInvokedData;
    const step: RunStepResponse = {
      type: 'tool_call',
      iteration: si.iteration,
      timestamp: si.timestamp ?? now,
      toolName: '__subagent_spawn',
      toolCallId: `subagent-spawn-${si.iteration}`,
      input: { tasks: si.tasks, count: si.count, mode: si.mode },
    };
    return {
      ...existing,
      steps: [...existing.steps, step],
      currentIteration: si.iteration,
      updatedAt: now,
    };
  }

  if (eventType === 'agent.run.subagent_started') {
    const ss = d as unknown as RunSubagentStartedData;
    const step: RunStepResponse = {
      type: 'tool_call',
      iteration: existing.currentIteration ?? 0,
      timestamp: ss.timestamp ?? now,
      toolName: '__subagent_run',
      toolCallId: ss.taskId,
      input: { agentId: ss.agentId, agentName: ss.agentName, childRunId: ss.runId },
    };
    return {
      ...existing,
      steps: [...existing.steps, step],
      updatedAt: now,
    };
  }

  if (eventType === 'agent.run.subagent_completed') {
    const sc = d as unknown as RunSubagentCompletedData;
    const output = JSON.stringify({
      agentName: sc.agentName,
      status: sc.success ? 'completed' : 'failed',
      durationMs: sc.durationMs,
      responseText: sc.response,
      error: sc.error,
      toolCalls: new Array(sc.toolCallCount ?? 0),
      childRunId: sc.runId,
    });
    const updatedSteps = existing.steps.map((s) => {
      if (s.type === 'tool_call' && s.toolName === '__subagent_run' && s.toolCallId === sc.taskId) {
        return { ...s, output, durationMs: sc.durationMs, error: sc.error };
      }
      return s;
    });
    return { ...existing, steps: updatedSteps, updatedAt: now };
  }

  if (eventType === 'agent.run.subagent_returned') {
    // Backfill prompt and childRunId into matching __subagent_run steps by taskId.
    const sr = d as unknown as RunSubagentReturnedData;
    const taskMap = new Map(sr.tasks.map((t) => [t.taskId, t]));
    const updatedSteps = existing.steps.map((s) => {
      if (s.type === 'tool_call' && s.toolName === '__subagent_run' && s.toolCallId && taskMap.has(s.toolCallId)) {
        const task = taskMap.get(s.toolCallId)!;
        const input = { ...s.input, prompt: task.prompt, childRunId: task.childRunId };
        return { ...s, input };
      }
      return s;
    });
    return { ...existing, steps: updatedSteps, updatedAt: now };
  }

  if (eventType === 'agent.run.subagents_all_returned') {
    return { ...existing, updatedAt: now };
  }

  // ── Context compaction (UI-state only; not surfaced in the trace) ─────────
  if (
    eventType === 'agent.run.context_compaction_start' ||
    eventType === 'agent.run.context_compaction_stop'
  ) {
    return { ...existing, updatedAt: now };
  }

  // ── Terminal events ───────────────────────────────────────────────────────
  if (eventType === 'agent.run.completed') {
    const cd = d as unknown as RunCompletedData;
    // Prefer the richer steps source: existing (built from streaming events) or
    // cd.steps (from the completion payload, used as fallback when streaming was missed).
    const steps = existing.steps.length >= (cd.steps?.length ?? 0)
      ? existing.steps
      : cd.steps;
    const derivedToolCalls = stepsToToolCalls(steps).map((tc) => ({
      ...tc,
      status: (tc.error ? 'error' : 'complete') as ToolCallDetail['status'],
    }));
    const derivedToolNames = [...new Set(derivedToolCalls.map((tc) => tc.toolName))];
    return {
      ...existing,
      status: 'completed',
      response: cd.response ?? existing.response,
      responsePreview: cd.response?.slice(0, PREVIEW_CAP) ?? existing.responsePreview,
      isStreaming: false,
      durationMs: cd.durationMs,
      // The completed event carries the authoritative aggregate usage; prefer it
      // over a per-iteration figure from final_answer_stop.
      usage: cd.usage ?? existing.usage,
      steps,
      toolCalls: derivedToolCalls,
      toolCallCount: derivedToolCalls.length,
      toolCallNames: derivedToolNames,
      updatedAt: now,
    };
  }

  if (eventType === 'agent.run.failed') {
    const fd = d as unknown as RunFailedData;
    return {
      ...existing,
      status: 'failed',
      error: fd.error,
      isStreaming: false,
      updatedAt: now,
    };
  }

  if (eventType === 'agent.run.cancelled') {
    return {
      ...existing,
      status: 'cancelled',
      isStreaming: false,
      updatedAt: now,
    };
  }

  return null;
}

/** Server-side filters for the runs list. `/me/runs` supports both natively
 *  (see `meApi.runs`), so a filtered view pages correctly instead of only
 *  narrowing whatever happened to be loaded. */
export interface UseRunsFilters {
  agentId?: string;
  status?: RunStatus;
}

export function useRuns(filters?: UseRunsFilters) {
  const { agentId, status } = filters ?? {};
  const { subscribe, isConnected } = usePlatformEvents();
  const [realtimeRuns, setRealtimeRuns] = useState<Map<string, RunSummary>>(new Map());
  const seenIdsRef = useRef(new Set<string>());

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    // Filters are part of the key so each combination gets its own cache
    // entry and its own pagination cursor.
    queryKey: ['me', 'runs', status ?? null, agentId ?? null],
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      return meApi.runs({ limit: PAGE_SIZE, cursor: pageParam, status, agentId });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextToken,
    staleTime: 0,
  });

  // Populate seen IDs from REST data
  useEffect(() => {
    if (data) {
      for (const page of data.pages) {
        for (const item of page.items) {
          seenIdsRef.current.add(item.runId);
        }
      }
    }
  }, [data]);

  const handleEvent = useCallback((event: PlatformEvent) => {
    const d = event.data as Record<string, unknown>;
    const eventType = event.eventType;

    // subagent_started/completed fire on the child run but should update the parent's trace.
    const isChildSubagentEvent =
      eventType === 'agent.run.subagent_started' ||
      eventType === 'agent.run.subagent_completed';

    const runId = isChildSubagentEvent
      ? (d.parentRunId as string | undefined)
      : (d.runId as string | undefined);

    if (!runId) return;

    const now = (event.metadata?.timestamp as string) ?? new Date().toISOString();

    setRealtimeRuns((prev) => {
      const next = new Map(prev);
      const existing = next.get(runId);

      if (eventType === 'agent.run.started') {
        if (seenIdsRef.current.has(runId)) return prev;
        seenIdsRef.current.add(runId);
        next.set(runId, {
          runId,
          agentId: (d.agentId as string) ?? '',
          agentName: d.agentName as string | undefined,
          conversationId: d.conversationId as string | undefined,
          status: 'running',
          responsePreview: '',
          isStreaming: false,
          toolCallCount: 0,
          toolCallNames: [],
          toolCalls: [],
          steps: [],
          createdAt: now,
          updatedAt: now,
          runtime: d.runtime as import('../types').RunRuntime | undefined,
        });
      } else if (existing) {
        const updated = applyEvent(existing, eventType, d, now);
        if (updated) {
          next.set(runId, updated);
        } else {
          return prev;
        }
      } else {
        // Unknown run — synthesize a fresh skeleton from this event's payload
        // and apply the event on top. No API fetch.
        seenIdsRef.current.add(runId);
        const skeleton = synthesizeRunFromEvent(runId, eventType, d, now);
        const updated = applyEvent(skeleton, eventType, d, now) ?? skeleton;
        next.set(runId, updated);
      }

      // Cap realtime map
      if (next.size > REALTIME_CAP) {
        const entries = [...next.entries()];
        return new Map(entries.slice(entries.length - REALTIME_CAP));
      }

      return next;
    });
  }, []);

  useEffect(() => {
    // Cross-space "my runs" list. lifecycle tier drops the high-frequency
    // step/delta stream; full per-run detail is the RunDetailPanel's job (it
    // subscribes by runId on selection). We ALSO take the single, low-volume
    // `final_answer_stop` event (steps-tier, so requested explicitly) because it
    // carries the run's result `content` + `usage` — the lifecycle `completed`
    // event does not, so without this the result line would be blank for runs
    // that arrive purely via events.
    const unsub = subscribe(
      [
        { eventType: 'agent.run.*', level: 'lifecycle' },
        { eventType: 'agent.run.final_answer_stop' },
      ],
      handleEvent,
    );
    return unsub;
  }, [subscribe, handleEvent]);

  // Merge: realtime runs first, then REST, dedup by runId
  const restItems = data?.pages.flatMap((p) => p.items.map(statusResponseToSummary)) ?? [];
  const realtimeList = [...realtimeRuns.values()].reverse(); // newest first
  const realtimeIds = new Set(realtimeList.map((r) => r.runId));
  const dedupedRest = restItems.filter((r) => !realtimeIds.has(r.runId));
  const runs = [...realtimeList, ...dedupedRest];

  return {
    runs,
    isLoading,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    realtimeCount: realtimeRuns.size,
    isConnected,
  };
}
